import React, { useState, useEffect } from "react";
import { useAuth } from '../contexts/AuthContext';
import Payment from './Payment';
import SubscriptionManager from './SubscriptionManager';
import "./NetflixUI.css";

const NetflixUI = () => {
  const { 
    currentUser, 
    userProfile, 
    logout, 
    isPremium, 
    hasFullAccess, 
    daysRemaining,
    isFreeTrialExpired 
  } = useAuth();
  
  const [showPayment, setShowPayment] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSubscriptionManager, setShowSubscriptionManager] = useState(false);

  // Auto-show payment modal from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('showPayment') === 'true') {
      setShowPayment(true);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  const categories = [
    {
      name: "Reflex & Aim",
      games: [
        {
          id: "bird-hunter",
          name: "Sky Strike",
          description: "Shoot birds with hand gestures",
          thumbnail: "🎯",
          isLocked: false,
        },
        {
          id: "balloon-pop",
          name: "Balloon Pop",
          description: "Pop balloons with both hands",
          thumbnail: "🎈",
          isLocked: false,
        },
        {
          id: "whack-a-mole",
          name: "Whack a Mole",
          description: "How many moles can you whack",
          thumbnail: "🦫",
          isLocked: false,
        },
        {
          id: "sand-drawing",
          name: "Sand Drawing",
          description: "Draw an art on the sand",
          thumbnail: "🖌️",
          isLocked: true,
        },
        {
          id: "opposite-reflexes",
          name: "Simon Says Opposites",
          description: "How fast are your reflexes!",
          thumbnail: "⚡",
          isLocked: true,
        },
      ],
    },
    {
      name: "Thinking & Logic",
      games: [
        {
          id: "word-slap",
          name: "Word Slap",
          description: "Form the right word",
          thumbnail: "🎲",
          isLocked: true,
        },
        {
          id: "chemistry-man",
          name: "MistryMan",
          description: "Hunt down right chemistry formulas",
          thumbnail: "🧪",
          isLocked: true,
        },
        {
          id: "math-rush",
          name: "Math Rush",
          description: "How fast are you in basic math!",
          thumbnail: "🧮",
          isLocked: true,
        },
        {
          id: "flag-quiz",
          name: "Flag Quiz",
          description: "Identify the country flag!",
          thumbnail: "🏳️",
          isLocked: true,
        },
        {
          id: "spelling-bee",
          name: "Spelling Bee",
          description: "Spell the words outloud.",
          thumbnail: "🐝",
          isLocked: true,
        },
        {
          id: "guess-in-10",
          name: "Guess in 10",
          description: "Can you guess the right animal?",
          thumbnail: "🦁",
          isLocked: true,
        },
      ],
    },
    {
      name: "Move & Play",
      games: [
        {
          id: "excercise-detector",
          name: "Excercise Detector",
          description: "Full excericise to upper body",
          thumbnail: "💪",
          isLocked: true,
        },
        {
          id: "space-battle",
          name: "Space Battle",
          description: "Survive the space",
          thumbnail: "🚀",
          isLocked: true,
        },
        {
          id: "dodge-ball",
          name: "Dodge Ball",
          description: "Dodge incoming obstacles",
          thumbnail: "⚽",
          isLocked: true,
        },
        {
          id: "simon-says",
          name: "Simon Says",
          description: "How fast are your reflexes!",
          thumbnail: "⚡",
          isLocked: true,
        },
        {
          id: "kids-workout-1",
          name: "Move & Groove Kids",
          description: "Have fun working out, kids!",
          thumbnail: "🏃",
          isLocked: true,
        },
        {
          id: "fruit-catcher",
          name: "Don't Eat Veggies",
          description: "Get fruits, avoid veggies",
          thumbnail: "🍎",
          isLocked: true,
        },
        {
          id: "number-smash",
          name: "Number Smash",
          description: "Smash the right number",
          thumbnail: "🔨",
          isLocked: true,
        },
        {
          id: "memory-match",
          name: "Memory Match",
          description: "Test your memory",
          thumbnail: "🧠",
          isLocked: true,
        },
      ],
    },
    {
      name: "Two Players",
      games: [
        {
          id: "dodge-ball-two-player",
          name: "Two Player Dodge Ball",
          description: "Two players dodge incoming obstacles",
          thumbnail: "⚽ ⚽",
          isLocked: true,
        },
        {
          id: "dance-battle",
          name: "Dance Battle",
          description: "Two player dance off",
          thumbnail: "💃🏽",
          isLocked: true,
        },
        {
          id: "color-stack",
          name: "Color Stack Battle",
          description: "Stack the right colors to win",
          thumbnail: "🎨",
          isLocked: true,
        },
      ],
    },
  ];

  const handleGameClick = (gameId, isLocked) => {
    console.log('🎮 Game click:', { gameId, isLocked, hasFullAccess, isPremium, daysRemaining });

    // Check 1: User must be logged in
    if (!currentUser) {
      alert('Please log in to play games!');
      return;
    }

    // Check 2: If game is locked, check access
    if (isLocked && !hasFullAccess) {
      setShowPayment(true);
      return;
    }
    
    // All checks passed - navigate to game
    window.location.href = `/games/${gameId}.html`;
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  return (
    <div className="netflix-container">
      {/* Header */}
      <header className="netflix-header">
        <div className="logo">
          <h1>Vision Games</h1>
          <p>Play Games by Moving Your Body</p>
        </div>
        <div className="header-actions">
          {!isPremium && (
            <button 
              className="premium-button"
              onClick={() => setShowPayment(true)}
            >
              ⭐ Get Premium
            </button>
          )}
          {isPremium && (
            <>
              <span className="premium-badge">✨ Premium</span>
              <button 
                className="manage-subscription-button"
                onClick={() => setShowSubscriptionManager(true)}
              >
                ⚙️ Manage
              </button>
            </>
          )}
          <div className="user-menu-container">
            <button 
              className="user-button"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              {currentUser?.email?.charAt(0).toUpperCase() || '👤'}
            </button>
            {showUserMenu && (
              <div className="user-dropdown">
                <div className="user-info">
                  <p className="user-email">{currentUser?.email}</p>
                  {userProfile?.displayName && (
                    <p className="user-name">{userProfile.displayName}</p>
                  )}
                </div>
                {isPremium && (
                  <button 
                    onClick={() => {
                      setShowSubscriptionManager(true);
                      setShowUserMenu(false);
                    }} 
                    className="manage-button"
                  >
                    Manage Subscription
                  </button>
                )}
                <button onClick={handleLogout} className="logout-button">
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* PERSISTENT FREE TRIAL BANNER - ALWAYS VISIBLE */}
      {currentUser && !isPremium && daysRemaining > 0 && (
        <div className="persistent-trial-banner">
          <div className="trial-banner-content">
            <div className="trial-info">
              <span className="trial-icon">🎉</span>
              <div className="trial-text">
                <h3>Free Trial Active</h3>
                <p>
                  <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining</strong>
                  {' '}to enjoy all games for free
                </p>
              </div>
            </div>
            <button 
              className="trial-subscribe-button"
              onClick={() => setShowPayment(true)}
            >
              Subscribe Now
            </button>
          </div>
        </div>
      )}

      {/* TRIAL EXPIRATION WARNING (Last 3 days) */}
      {currentUser && !isPremium && daysRemaining > 0 && daysRemaining <= 3 && (
        <div className="trial-warning-urgent">
          <span className="warning-icon">⚠️</span>
          <span>
            Only <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong> left! 
            Subscribe now to keep unlimited access.
          </span>
          <button onClick={() => setShowPayment(true)}>
            View Plans
          </button>
        </div>
      )}

      {/* TRIAL EXPIRED MESSAGE */}
      {currentUser && isFreeTrialExpired && (
        <div className="trial-expired-banner">
          <div className="expired-content">
            <span className="expired-icon">🔒</span>
            <div className="expired-text">
              <h3>Free Trial Ended</h3>
              <p>Subscribe to continue playing all games. You still have access to 3 free games!</p>
            </div>
            <button 
              className="expired-subscribe-button"
              onClick={() => setShowPayment(true)}
            >
              Subscribe Now
            </button>
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="categories-section">
        {categories.map((category, idx) => (
          <div key={idx} className="category">
            <h2 className="category-title">
              {category.name} ({category.games.length})
            </h2>
            <div className="games-row">
              <div className="games-scroll">
                {category.games.map((game) => {
                  const isAccessible = !game.isLocked || hasFullAccess;
                  
                  return (
                    <div
                      key={game.id}
                      className={`game-tile ${!isAccessible ? 'locked' : ''}`}
                      onClick={() => handleGameClick(game.id, game.isLocked)}
                    >
                      <div className="game-tile-content">
                        <div className="game-thumbnail">{game.thumbnail}</div>
                        <div className="game-info">
                          <h3>
                            {game.name}
                            {!isAccessible && (
                              <span className="lock-icon">🔒</span>
                            )}
                          </h3>
                          <p>{game.description}</p>
                        </div>
                      </div>
                      <div className="game-tile-overlay">
                        <button className="play-button">
                          {!isAccessible ? '🔒 UNLOCK' : '▶ PLAY'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <footer className="netflix-footer">
        <p>© 2026 Vision Games. All rights reserved.</p>
      </footer>

      {/* Payment Modal */}
      {showPayment && <Payment onClose={() => setShowPayment(false)} />}
      
      {/* Subscription Manager */}
      {showSubscriptionManager && (
        <SubscriptionManager onClose={() => setShowSubscriptionManager(false)} />
      )}
    </div>
  );
};

export default NetflixUI;